import React, { useCallback, useMemo, useState } from 'react';
import { DataFilterField, IDataFilterFieldCommonProps } from './DataFilterField';
import { CountFilterValue, newCountFilterValue } from '@decisions/server/DecisionsFramework/Design/Page/Filters';
import { getNumberFromFormattedString, initializeNumericValue, parseInputToNumber } from 'utils/NumericFormattingUtils';
import debounce from 'debounce';
// import './CountDataFilterField.less'

export interface ICountDataFilterFieldProps extends IDataFilterFieldCommonProps<CountFilterValue> {}

/**
 * Numeric filter editor that produces a single value instead of a range definition.
 * e.g. just having a count you want to take of the total results.
 *
 * Copyright (c) 2021 Decisions All Rights Reserved
 */
export function CountDataFilterField({ onChange, personalization, validation, value }: ICountDataFilterFieldProps) {
  const formatValue = useCallback((value: string) => {
    return parseInputToNumber(value, false, true);
  }, []);
  const initialValue = initializeNumericValue(value.Value ? value.Value.toString() : '', true);
  const [stringValue, setStringValue] = useState(formatValue(initialValue));
  const onValueChange = useCallback(
    (newValue: number | null) => {
      onChange(
        newCountFilterValue({
          ...value,
          Value: newValue || undefined,
        })
      );
    },
    [onChange, value]
  );
  const emitCountChange = useMemo(() => debounce(onValueChange, 500), [onValueChange]);

  const setFieldValue = useCallback(
    (stringValue: string) => {
      emitCountChange.clear();
      const formatted = formatValue(stringValue);
      setStringValue(formatted ?? '');
      const parsed = getNumberFromFormattedString(formatted || '', false, true);
      emitCountChange(parsed);
    },
    [emitCountChange, formatValue]
  );

  const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    emitCountChange.clear();
    const stringValue = e.target.value;
    const formatted = formatValue(stringValue);
    setStringValue(formatted ?? '');
    const parsed = getNumberFromFormattedString(formatted || '', false, true);
    onValueChange(parsed);
  };

  return (
    <DataFilterField
      value={value}
      fieldText={stringValue || ''}
      onChange={onChange}
      onBlur={onBlur}
      onFieldChange={setFieldValue}
      validation={validation}
      personalization={personalization}
      placeholder="All"
    />
  );
}
